import { combineClass, type JSX } from 'cdui-js';
import { createEffect, createSignal, mergeProps, onCleanup, onMount, Show } from 'solid-js';
import { Portal } from 'solid-js/web';

type Placement = 'top' | 'bottom' | 'left' | 'right';

export type DiFiTooltipProps = {
  /** 触发方式 */
  trigger?: 'click' | 'hover';
  /** 提示内容 */
  content: JSX.Element | string;
  /** 触发元素 */
  children: JSX.Element;
  /** 首选弹出方向，默认 top；空间不足时自动翻转（可用 autoFlip 关闭） */
  placement?: Placement;
  /**
   * 空间不足时是否自动翻转到其他方向
   * @default true
   */
  autoFlip?: boolean;
  /** tooltip 外层额外 class */
  class?: string;
  /** tooltip 外层额外 style */
  style?: JSX.CSSProperties;
  /** 弹出内容区域额外 style */
  contentStyle?: JSX.CSSProperties;
  /** hover 模式下延迟显示 (ms) */
  showDelay?: number;
  /** hover 模式下延迟隐藏 (ms) */
  hideDelay?: number;
  /** 弹出框与触发元素的间距 (px) */
  offset?: number;
  /**
   * 为 true 时触发区域不超出父级宽度，超出部分单行省略号（flex 子项需配合 min-width:0，外层已处理）
   * @default true
   */
  ellipsis?: boolean;
};

const ARROW_SIZE = 6;
const VIEWPORT_PADDING = 8;
const ARROW_EDGE_PADDING = 10;
const DEFAULT_MAX_WIDTH = 260;

export const DiFiTooltip = (rawProps: DiFiTooltipProps) => {
  const props = mergeProps(
    {
      trigger: 'click' as const,
      placement: 'top' as Placement,
      autoFlip: true,
      showDelay: 0,
      hideDelay: 150,
      offset: 8,
      ellipsis: true,
    },
    rawProps,
  );

  const [visible, setVisible] = createSignal(false);
  const [pos, setPos] = createSignal<{ top: number; left: number }>({ top: 0, left: 0 });
  const [actualPlacement, setActualPlacement] = createSignal<Placement>('top');
  const [arrowOffset, setArrowOffset] = createSignal(0);
  const [popupMaxWidth, setPopupMaxWidth] = createSignal(DEFAULT_MAX_WIDTH);

  let triggerRef: HTMLDivElement | undefined;
  let tooltipRef: HTMLDivElement | undefined;
  let showTimer: ReturnType<typeof setTimeout> | undefined;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;

  const clearTimers = () => {
    if (showTimer) { clearTimeout(showTimer); showTimer = undefined; }
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = undefined; }
  };

  const computePosition = () => {
    if (!triggerRef || !tooltipRef) return;

    const triggerRect = triggerRef.getBoundingClientRect();
    const gap = props.offset + ARROW_SIZE;

    const spaceTop = triggerRect.top;
    const spaceBottom = window.innerHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;
    const spaceRight = window.innerWidth - triggerRect.right;

    let resolved = props.placement;
    if (props.autoFlip) {
      const tooltipRect = tooltipRef.getBoundingClientRect();
      const fits = (p: Placement) => {
        switch (p) {
          case 'top': return spaceTop >= tooltipRect.height + gap + VIEWPORT_PADDING;
          case 'bottom': return spaceBottom >= tooltipRect.height + gap + VIEWPORT_PADDING;
          case 'left': return spaceLeft >= tooltipRect.width + gap + VIEWPORT_PADDING;
          case 'right': return spaceRight >= tooltipRect.width + gap + VIEWPORT_PADDING;
        }
      };

      const opposite: Record<Placement, Placement> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
      const fallbackOrder: Placement[] = ['top', 'bottom', 'right', 'left'];

      if (!fits(resolved)) {
        resolved = opposite[resolved];
        if (!fits(resolved)) {
          resolved = fallbackOrder.find(fits) ?? props.placement;
        }
      }
    }

    // left/right 在窄屏下压缩 max-width，保证仍能落在触发点一侧，箭头方向不变
    let maxWidth = DEFAULT_MAX_WIDTH;
    if (resolved === 'left') {
      maxWidth = Math.max(80, Math.min(DEFAULT_MAX_WIDTH, spaceLeft - gap - VIEWPORT_PADDING));
    } else if (resolved === 'right') {
      maxWidth = Math.max(80, Math.min(DEFAULT_MAX_WIDTH, spaceRight - gap - VIEWPORT_PADDING));
    }
    setPopupMaxWidth(maxWidth);
    // 同步写到 DOM，避免等 Solid 重绘后才量到正确宽度
    tooltipRef.style.maxWidth = `${maxWidth}px`;

    const tooltipRect = tooltipRef.getBoundingClientRect();
    let top = 0;
    let left = 0;

    switch (resolved) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - gap;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + gap;
        break;
    }

    left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - tooltipRect.width - VIEWPORT_PADDING));
    top = Math.max(VIEWPORT_PADDING, Math.min(top, window.innerHeight - tooltipRect.height - VIEWPORT_PADDING));

    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    const clampArrow = (value: number, size: number) =>
      Math.max(ARROW_EDGE_PADDING, Math.min(value, size - ARROW_EDGE_PADDING));

    let nextArrowOffset = 0;
    if (resolved === 'top' || resolved === 'bottom') {
      nextArrowOffset = clampArrow(triggerCenterX - left, tooltipRect.width);
    } else {
      nextArrowOffset = clampArrow(triggerCenterY - top, tooltipRect.height);
    }

    setActualPlacement(resolved);
    setArrowOffset(nextArrowOffset);
    setPos({ top, left });
  };

  const show = () => {
    clearTimers();
    const delay = props.trigger === 'hover' ? props.showDelay : 0;
    showTimer = setTimeout(() => {
      setVisible(true);
      requestAnimationFrame(computePosition);
    }, delay);
  };

  const hide = () => {
    clearTimers();
    const delay = props.trigger === 'hover' ? props.hideDelay : 0;
    hideTimer = setTimeout(() => setVisible(false), delay);
  };

  const toggle = () => (visible() ? hide() : show());

  const onClickOutside = (e: MouseEvent) => {
    if (
      props.trigger === 'click' &&
      visible() &&
      triggerRef && !triggerRef.contains(e.target as Node) &&
      tooltipRef && !tooltipRef.contains(e.target as Node)
    ) {
      hide();
    }
  };

  onMount(() => {
    document.addEventListener('mousedown', onClickOutside, true);
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    onCleanup(() => {
      clearTimers();
      document.removeEventListener('mousedown', onClickOutside, true);
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    });
  });

  createEffect(() => {
    if (!visible()) return;
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        computePosition();
        requestAnimationFrame(computePosition);
      });
    });
  });

  const arrowStyle = (): JSX.CSSProperties => {
    const size = `${ARROW_SIZE}px`;
    const base: JSX.CSSProperties = {
      position: 'absolute',
      width: 0,
      height: 0,
      'border-style': 'solid',
    };
    const offset = `${arrowOffset()}px`;
    switch (actualPlacement()) {
      case 'top':
        return { ...base, bottom: `-${size}`, left: offset, transform: 'translateX(-50%)', 'border-width': `${size} ${size} 0 ${size}`, 'border-color': 'rgba(0,0,0,0.75) transparent transparent transparent' };
      case 'bottom':
        return { ...base, top: `-${size}`, left: offset, transform: 'translateX(-50%)', 'border-width': `0 ${size} ${size} ${size}`, 'border-color': 'transparent transparent rgba(0,0,0,0.75) transparent' };
      case 'left':
        return { ...base, right: `-${size}`, top: offset, transform: 'translateY(-50%)', 'border-width': `${size} 0 ${size} ${size}`, 'border-color': 'transparent transparent transparent rgba(0,0,0,0.75)' };
      case 'right':
        return { ...base, left: `-${size}`, top: offset, transform: 'translateY(-50%)', 'border-width': `${size} ${size} ${size} 0`, 'border-color': 'transparent rgba(0,0,0,0.75) transparent transparent' };
    }
  };

  const triggerEllipsisStyle: JSX.CSSProperties = props.ellipsis
    ? {
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
      'white-space': 'nowrap',
    }
    : {};

  return (
    <div
      class={combineClass('difi-tooltip-wrapper', props.class)}
      style={{
        display: 'block',
        position: 'relative',
        width: '100%',
        'max-width': '100%',
        'min-width': 0,
        ...props.style,
      }}
    >
      <div
        ref={(el) => (triggerRef = el)}
        class="difi-tooltip-trigger"
        style={{
          display: 'block',
          width: '100%',
          'max-width': '100%',
          'min-width': 0,
          ...triggerEllipsisStyle,
        }}
        onclick={props.trigger === 'click' ? () => toggle() : undefined}
        onmouseenter={props.trigger === 'hover' ? () => show() : undefined}
        onmouseleave={props.trigger === 'hover' ? () => hide() : undefined}
      >
        {props.children}
      </div>

      <Show when={visible()}>
        <Portal mount={document.body}>
          <div
            ref={(el) => {
              tooltipRef = el ?? undefined;
              if (el) requestAnimationFrame(computePosition);
            }}
            class="difi-tooltip-popup"
            style={{
              position: 'fixed',
              'z-index': 9999,
              top: `${pos().top}px`,
              left: `${pos().left}px`,
              background: 'rgba(0,0,0,0.75)',
              color: '#fff',
              'border-radius': '6px',
              padding: '8px 12px',
              'font-size': '13px',
              'line-height': '1.4',
              'max-width': `${popupMaxWidth()}px`,
              'word-break': 'break-word',
              'box-shadow': '0 2px 8px rgba(0,0,0,0.2)',
              'pointer-events': 'auto',
              ...props.contentStyle,
            }}
            onmouseenter={props.trigger === 'hover' ? () => clearTimers() : undefined}
            onmouseleave={props.trigger === 'hover' ? () => hide() : undefined}
          >
            <div style={arrowStyle()} />
            {props.content}
          </div>
        </Portal>
      </Show>
    </div>
  );
};
